   // src/pages/AdminPanel.tsx
   import React, { useEffect, useState } from 'react';
   import { useApi } from '../services/api';
   import { useNavigate } from 'react-router-dom';
   import { 
  Typography, Box, Paper, Table, TableBody, 
  TableCell, TableContainer, TableHead, TableRow,
  Button, CircularProgress, Tabs, Tab,
  Card, CardContent, CardActions,
  LinearProgress, IconButton, Chip, Avatar, Divider,
  TextField, Alert
} from '@mui/material';
   import { GridContainer, GridItem } from '../components/CustomGrid';
   import AddIcon from '@mui/icons-material/Add';
   import EditIcon from '@mui/icons-material/Edit';
   import DeleteIcon from '@mui/icons-material/Delete';
   import VisibilityIcon from '@mui/icons-material/Visibility';
   import LocalFloristIcon from '@mui/icons-material/LocalFlorist';
   import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
   import CategoryIcon from '@mui/icons-material/Category';
   import PeopleIcon from '@mui/icons-material/People';
import EmailIcon from '@mui/icons-material/Email';
   
   interface TabPanelProps {
     children?: React.ReactNode;
     index: number;
     value: number;
   }
   
   const TabPanel = (props: TabPanelProps) => {
     const { children, value, index, ...other } = props;
   
     return (
       <div
         role="tabpanel"
         hidden={value !== index}
         id={`admin-tabpanel-${index}`}
         aria-labelledby={`admin-tab-${index}`}
         {...other}
       >
         {value === index && (
           <Box sx={{ p: 3 }}>
             {children}
           </Box>
         )}
       </div>
     );
   };
   
   interface User {
     id: number;
     name: string;
     email: string;
     plantCount: number;
     lastActive: string;
   }
   
   interface SystemStats {
     totalUsers: number;
     totalPlants: number;
     activeUsers: number;
     newUsersThisMonth: number;
   }
   
   const AdminPanel: React.FC = () => {
     const api = useApi();
     const navigate = useNavigate();
     const [tabValue, setTabValue] = useState(0);
       const [plants, setPlants] = useState<any[]>([]);
  const [users, setUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteSuccess, setInviteSuccess] = useState<string | null>(null);
     
     useEffect(() => {
       const fetchData = async () => {
         try {
           setLoading(true);
           const plantsData = await api.getPlants();
           setPlants(plantsData);
           
           // Uncomment when API is ready
           // const usersData = await api.getAllUsers();
           // setUsers(usersData);
           
           // Mock users data for now
           setUsers([
             { email: 'admin@palmgardenrs.com', name: 'Admin User', isAdmin: true },
             { email: 'user1@example.com', name: 'Regular User', isAdmin: false },
             { email: 'user2@example.com', name: 'Another User', isAdmin: false },
           ]);
         } catch (err) {
           console.error('Error fetching admin data:', err);
           setError("Failed to load admin data");
         } finally {
           setLoading(false);
         }
       };
       
       fetchData();
     }, [api]);
     
       const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleGenerateInvite = async () => {
    if (!inviteEmail.trim()) {
      setError('Email is required');
      return;
    }

    setInviteLoading(true);
    setError(null);
    setInviteSuccess(null);

    try {
      const response = await fetch('https://auth.palmgardenrs.com/api/auth/generate-invite', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: inviteEmail }),
      });

      const data = await response.json();

      if (data.success) {
        setInviteSuccess(`Invite code generated: ${data.inviteCode}`);
        setInviteEmail('');
      } else {
        setError(data.message || 'Failed to generate invite code');
      }
    } catch (err) {
      setError('Failed to generate invite code');
    } finally {
      setInviteLoading(false);
    }
  };
     
     if (loading) {
       return (
         <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
           <CircularProgress />
         </Box>
       );
     }
     
     if (error) {
       return (
         <Box sx={{ p: 3 }}>
           <Typography color="error" variant="h6">
             {error}
           </Typography>
           <Button 
             variant="contained" 
             onClick={() => window.location.reload()}
             sx={{ mt: 2 }}
           >
             Retry
           </Button>
         </Box>
       );
     }
     
     const totalPlants = plants.length;
     const publishedPlants = plants.filter(plant => plant.published).length;
     const unpublishedPlants = totalPlants - publishedPlants;
     const categories = new Set(plants.map(plant => plant.type)).size;
     
     return (
       <Box sx={{ py: 4 }}>
         <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
           <Box>
             <Typography variant="h4" component="h1" gutterBottom>
               🌿 Plant Database Admin
             </Typography>
             <Typography variant="subtitle1" color="text.secondary">
               Manage and publish plant information to the public site
             </Typography>
           </Box>
           <Button 
             variant="contained" 
             startIcon={<AddIcon />}
             onClick={() => navigate('/plants/add')}
           >
             ADD NEW PLANT
           </Button>
         </Box>
         
         <Box sx={{ mb: 4 }}>
           <Typography variant="h6" gutterBottom>
             Database Completeness
           </Typography>
           <LinearProgress 
             variant="determinate" 
             value={79} 
             sx={{ height: 10, borderRadius: 5 }}
           />
           <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
             <Typography variant="body2" color="text.secondary">
               79%
             </Typography>
           </Box>
         </Box>
         
         <Box sx={{ mb: 4 }}>
           <GridContainer spacing={3}>
             <GridItem xs={12} sm={6} md={3}>
               <Card sx={{ height: '100%' }}>
                 <CardContent>
                   <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                     <Avatar sx={{ bgcolor: 'primary.main', mr: 2 }}>
                       <LocalFloristIcon />
                     </Avatar>
                     <Typography variant="h6">
                       Total Plants
                     </Typography>
                   </Box>
                   <Typography variant="h3" component="div" sx={{ fontWeight: 'bold' }}>
                     {totalPlants}
                   </Typography>
                 </CardContent>
               </Card>
             </GridItem>
             
             <GridItem xs={12} sm={6} md={3}>
               <Card sx={{ height: '100%' }}>
                 <CardContent>
                   <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                     <Avatar sx={{ bgcolor: 'success.main', mr: 2 }}>
                       <VisibilityIcon />
                     </Avatar>
                     <Typography variant="h6">
                       Published
                     </Typography>
                   </Box>
                   <Typography variant="h3" component="div" sx={{ fontWeight: 'bold' }}>
                     {publishedPlants}
                   </Typography>
                 </CardContent>
               </Card>
             </GridItem>
             
             <GridItem xs={12} sm={6} md={3}>
               <Card sx={{ height: '100%' }}>
                 <CardContent>
                   <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                     <Avatar sx={{ bgcolor: 'warning.main', mr: 2 }}>
                       <VisibilityOffIcon />
                     </Avatar>
                     <Typography variant="h6">
                       Unpublished
                     </Typography>
                   </Box>
                   <Typography variant="h3" component="div" sx={{ fontWeight: 'bold' }}>
                     {unpublishedPlants}
                   </Typography>
                 </CardContent>
               </Card>
             </GridItem>
             
             <GridItem xs={12} sm={6} md={3}>
               <Card sx={{ height: '100%' }}>
                 <CardContent>
                   <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                     <Avatar sx={{ bgcolor: 'info.main', mr: 2 }}>
                       <CategoryIcon />
                     </Avatar>
                     <Typography variant="h6">
                       Categories
                     </Typography>
                   </Box>
                   <Typography variant="h3" component="div" sx={{ fontWeight: 'bold' }}>
                     {categories}
                   </Typography>
                 </CardContent>
               </Card>
             </GridItem>
           </GridContainer>
         </Box>
         
         <Paper sx={{ mb: 4 }}>
           <Tabs 
             value={tabValue} 
             onChange={handleTabChange}
             sx={{ borderBottom: 1, borderColor: 'divider' }}
           >
             <Tab label="Plants" />
             <Tab label="Users" />
             <Tab label="Invites" />
             <Tab label="Statistics" />
           </Tabs>
           
           <TabPanel value={tabValue} index={0}>
             {loading ? (
               <LinearProgress />
             ) : (
               <TableContainer>
                 <Table>
                   <TableHead>
                     <TableRow>
                       <TableCell>ID</TableCell>
                       <TableCell>Name</TableCell>
                       <TableCell>Type</TableCell>
                       <TableCell>Status</TableCell>
                       <TableCell align="right">Actions</TableCell>
                     </TableRow>
                   </TableHead>
                   <TableBody>
                     {plants.map((plant) => (
                       <TableRow key={plant.id}>
                         <TableCell>{plant.id}</TableCell>
                         <TableCell>{plant.name}</TableCell>
                         <TableCell>{plant.type}</TableCell>
                         <TableCell>
                           <Chip 
                             label={plant.published ? "Published" : "Draft"} 
                             color={plant.published ? "success" : "default"}
                             size="small"
                           />
                         </TableCell>
                         <TableCell align="right">
                           <IconButton 
                             size="small" 
                             onClick={() => navigate(`/plants/${plant.id}`)}
                           >
                             <VisibilityIcon fontSize="small" />
                           </IconButton>
                           <IconButton 
                             size="small" 
                             onClick={() => navigate(`/plants/edit/${plant.id}`)}
                           >
                             <EditIcon fontSize="small" />
                           </IconButton>
                           <IconButton size="small" color="error">
                             <DeleteIcon fontSize="small" />
                           </IconButton>
                         </TableCell>
                       </TableRow>
                     ))}
                   </TableBody>
                 </Table>
               </TableContainer>
             )}
           </TabPanel>
           
           <TabPanel value={tabValue} index={1}>
             <TableContainer>
               <Table>
                 <TableHead>
                   <TableRow>
                     <TableCell>Email</TableCell>
                     <TableCell>Name</TableCell>
                     <TableCell>Role</TableCell>
                     <TableCell align="right">Actions</TableCell>
                   </TableRow>
                 </TableHead>
                 <TableBody>
                   {users.map((user) => (
                     <TableRow key={user.email}>
                       <TableCell>{user.email}</TableCell>
                       <TableCell>{user.name}</TableCell>
                       <TableCell>
                         <Chip 
                           label={user.isAdmin ? "Admin" : "User"} 
                           color={user.isAdmin ? "primary" : "default"}
                           size="small"
                         />
                       </TableCell>
                       <TableCell align="right">
                         <IconButton size="small">
                           <EditIcon fontSize="small" />
                         </IconButton>
                       </TableCell>
                     </TableRow>
                   ))}
                 </TableBody>
               </Table>
             </TableContainer>
           </TabPanel>
           
           <TabPanel value={tabValue} index={2}>
             <Box sx={{ p: 3 }}>
               <Typography variant="h6" gutterBottom>
                 Generate Invite Codes
               </Typography>
               <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                 Create invite codes for new users. Only users with valid invite codes can sign up.
               </Typography>
               
               <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-end', mb: 3 }}>
                 <TextField
                   label="Email Address"
                   value={inviteEmail}
                   onChange={(e) => setInviteEmail(e.target.value)}
                   placeholder="user@example.com"
                   sx={{ flexGrow: 1 }}
                   disabled={inviteLoading}
                 />
                 <Button
                   variant="contained"
                   startIcon={<EmailIcon />}
                   onClick={handleGenerateInvite}
                   disabled={inviteLoading || !inviteEmail.trim()}
                 >
                   {inviteLoading ? <CircularProgress size={20} /> : 'Generate Invite'}
                 </Button>
               </Box>
               
               {inviteSuccess && (
                 <Alert severity="success" sx={{ mb: 2 }}>
                   {inviteSuccess}
                 </Alert>
               )}
               
               {error && (
                 <Alert severity="error" sx={{ mb: 2 }}>
                   {error}
                 </Alert>
               )}
               
               <Typography variant="body2" color="text.secondary">
                 <strong>Instructions:</strong>
                 <ol>
                   <li>Enter the email address of the user you want to invite</li>
                   <li>Click "Generate Invite" to create a unique invite code</li>
                   <li>Send the invite code to the user via email or message</li>
                   <li>The user can use this code to sign up for an account</li>
                 </ol>
               </Typography>
             </Box>
           </TabPanel>
           
           <TabPanel value={tabValue} index={3}>
             <Typography variant="h6" gutterBottom>
               Coming Soon
             </Typography>
             <Typography variant="body1">
               Detailed statistics and analytics will be available in a future update.
             </Typography>
           </TabPanel>
         </Paper>
       </Box>
     );
   };
   
   export default AdminPanel;